import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import HolidayPackageComposer from "containers/AddListingFlows/PageAddHolidayPackage/HolidayPackageComposer";
import type { MemoContent, DroppedItem } from "containers/AddListingFlows/PageAddHolidayPackage/HolidayPackageComposer";
import NotesModal from "containers/AddListingFlows/PageAddHolidayPackage/NotesModal";
import TimeModal from "containers/AddListingFlows/PageAddHolidayPackage/TimeModal";
import StayCardH from "components/StayCardH/StayCardH";
import ExperiencesCardH from "components/ExperiencesCardH/ExperiencesCardH";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Skeleton from "react-loading-skeleton";
import {
  selectEditingHolidayPackage,
  setEditingHolidayPackage
} from "application/features/holidayPackage/holidayPackageSlice";
import { selectCurrentHost } from "application/features/host/hostSlice";
import { useGetPropertiesByHostQuery } from "application/features/property/propertyApiSlice";
import { useGetExperiencesByHostUUIDQuery } from "application/features/experience/experienceApiSlice";
import { useNavigate } from "react-router-dom";

const ContentTab: React.FC<{ setIsDirty: (dirty: boolean) => void }> = ({ setIsDirty }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const editingHolidayPackage = useSelector(selectEditingHolidayPackage);
  const host = useSelector(selectCurrentHost);

  const [preparedEvents, setPreparedEvents] = useState<any[][]>([]);
  const [activeItem, setActiveItem] = useState<MemoContent | null>(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [modalNotes, setModalNotes] = useState("");
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [modalTime, setModalTime] = useState("");
  const [draggedItem, setDraggedItem] = useState<any>(null);
  const [showSkeleton, setShowSkeleton] = useState(true);

  // Fetch properties and experiences
  const { data: propertiesData = [], isLoading: propertiesLoading } = useGetPropertiesByHostQuery(host?.host_uuid);
  const { data: experiencesData = [], isLoading: experiencesLoading } = useGetExperiencesByHostUUIDQuery(host?.host_uuid);

  useEffect(() => {
    if (!propertiesLoading && !experiencesLoading) {
      setShowSkeleton(false);
    }
  }, [propertiesLoading, experiencesLoading]);

  // Initialize prepared events from package content
  useEffect(() => {
    if (editingHolidayPackage) {
      setPreparedEvents(prepareEvents(editingHolidayPackage?.package_content ? editingHolidayPackage.package_content : []));
    }
  }, [editingHolidayPackage]);

  const handleUpdateHolidayPackage = (field: string, value: any) => {
    dispatch(setEditingHolidayPackage({ ...editingHolidayPackage, [field]: value }));
    setIsDirty(true);
  };

  function prepareEvents(package_content: any) {
    const days: number[] = Array.from({ length: editingHolidayPackage?.duration_days || 1 }, (_, i) => i + 1);
    return days.map((dayIndex: number) => {
      const property = package_content.filter((item: MemoContent) => item.type === "property" && item.day === dayIndex);
      const experiences = package_content.filter((item: MemoContent) => item.type === "experience" && item.day === dayIndex);
      return [...property, ...experiences];
    });
  }

  function onDragStart(event: any) {
    let element = event.target;
    while (element && !element.getAttribute("data-uuid")) {
      element = element.parentElement;
    }
    if (!element) {
      return;
    }
    setDraggedItem({
      uuid: element.getAttribute("data-uuid"),
      name: element.getAttribute("data-title"),
      type: element.getAttribute("data-type") || "property",
    });
  }

  function onDrop(event: any): DroppedItem {
    const dayNumber = event.target.getAttribute("data-day-number");
    if (!draggedItem || !draggedItem.uuid || !draggedItem.name || !dayNumber) {
      return {
        success: false,
        data: null,
      };
    }
    const oldPackageContent = editingHolidayPackage?.package_content ? editingHolidayPackage.package_content : [];
    const data = {
      title: draggedItem.name,
      uuid: draggedItem.uuid,
      type: draggedItem.type,
      day: parseInt(dayNumber),
      time: "",
      notes: ""
    } as MemoContent;
    let newPackageContent = JSON.parse(JSON.stringify(oldPackageContent));
    newPackageContent.push(data);
    handleUpdateHolidayPackage("package_content", newPackageContent);
    setDraggedItem(null);
    setPreparedEvents(prepareEvents(newPackageContent));
    return {
      success: true,
      data: data,
    };
  }

  function onDeleteItem(item: MemoContent) {
    const oldPackageContent = editingHolidayPackage?.package_content ? editingHolidayPackage.package_content : [];
    let newPackageContent = JSON.parse(JSON.stringify(oldPackageContent));
    newPackageContent = newPackageContent.filter((content: MemoContent) => {
      return !(content.day === item.day && content.uuid === item.uuid);
    });
    handleUpdateHolidayPackage("package_content", newPackageContent);
    setPreparedEvents(prepareEvents(newPackageContent));
    toast.success("Elemento rimosso dal programma");
  }

  function onSetTime(item: MemoContent) {
    setModalTime(item.time || "");
    setActiveItem(item);
    setShowTimeModal(true);
  }

  function onSetNotes(item: MemoContent) {
    setModalNotes(item.notes || "");
    setActiveItem(item);
    setShowNotesModal(true);
  }

  const noteModalClose = (text: string) => {
    if (activeItem) {
      const oldPackageContent = editingHolidayPackage?.package_content ? editingHolidayPackage.package_content : [];
      let newPackageContent = JSON.parse(JSON.stringify(oldPackageContent));
      newPackageContent = newPackageContent.map((content: MemoContent) => {
        if (content.uuid === activeItem.uuid && content.day === activeItem.day) {
          return { ...content, notes: text };
        }
        return content;
      });
      handleUpdateHolidayPackage("package_content", newPackageContent);
      setPreparedEvents(prepareEvents(newPackageContent));
      toast.success("Note aggiornate");
    }
    setModalNotes("");
    setShowNotesModal(false);
    setActiveItem(null);
  };

  const timeModalClose = (text: string) => {
    if (activeItem) {
      const oldPackageContent = editingHolidayPackage?.package_content ? editingHolidayPackage.package_content : [];
      let newPackageContent = JSON.parse(JSON.stringify(oldPackageContent));
      newPackageContent = newPackageContent.map((content: MemoContent) => {
        if (content.uuid === activeItem.uuid && content.day === activeItem.day) {
          return { ...content, time: text };
        }
        return content;
      });
      handleUpdateHolidayPackage("package_content", newPackageContent);
      setPreparedEvents(prepareEvents(newPackageContent));
      toast.success("Orario aggiornato");
    }
    setModalTime("");
    setShowTimeModal(false);
    setActiveItem(null);
  };

  const handleAddNewProperty = () => {
    navigate('/host/new-property/add-listing-1');
  };

  const handleAddNewExperience = () => {
    navigate('/host/new-experience/add-listing-1');
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="w-full">
        <h2 className="text-2xl font-semibold">Contenuto del pacchetto</h2>
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
          Trascina proprietà ed esperienze negli giorni
        </p>
      </div>

      {/* Three Column Layout */}
      <div className="flex flex-row w-full gap-4 max-h-[calc(70vh)]">
        {/* Left Sidebar - Properties */}
        <div className="w-2/5 flex flex-col">
          <h3 className="text-lg font-semibold mb-2">
            Proprietà
          </h3>
          <div className="overflow-y-auto border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 flex-1">

            {propertiesLoading || showSkeleton ? (
              <div className="space-y-3">
                {Array.from(Array(3)).map((_, index) => (
                  <Skeleton key={`prop-skeleton-${index}`} className="w-full h-32" />
                ))}
              </div>
            ) : propertiesData && propertiesData.length > 0 ? (
              <div className="flex flex-col gap-3">
                {propertiesData.map((item: any, index: number) => (
                  <StayCardH
                    key={item.property_uuid || item.uuid || `property-${index}`}
                    data={item}
                    draggable={true}
                    onDragStart={onDragStart}
                    onDragEnd={() => { }}
                  />
                ))}
                <div className="w-full pt-4 border-t border-neutral-200 dark:border-neutral-700">
                  <ButtonPrimary onClick={handleAddNewProperty} className="w-full text-sm">
                    + Nuova proprietà
                  </ButtonPrimary>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-3">
                  Nessuna proprietà disponibile
                </p>
                <ButtonPrimary onClick={handleAddNewProperty} className="w-full text-sm">
                  + Aggiungi proprietà
                </ButtonPrimary>
              </div>
            )}
          </div>
        </div>

        {/* Center - Composer with Days */}
        <div id="calendar-view" className="w-1/5 overflow-y-auto">
          <HolidayPackageComposer
            title="Programma del pacchetto"
            description="Struttura il programma giorno per giorno"
            days={editingHolidayPackage?.duration_days ? editingHolidayPackage.duration_days : 1}
            onDrop={onDrop}
            onDeleteItem={onDeleteItem}
            onDragEnter={(event: any) => { }}
            onDragOver={(event: any) => { }}
            onDragLeave={(event: any) => { }}
            onSetNotes={onSetNotes}
            onSetTime={onSetTime}
            events={preparedEvents}
          />
        </div>

        {/* Right Sidebar - Experiences */}
        <div className="w-2/5 flex flex-col">
          <h3 className="text-lg font-semibold mb-2">
            Esperienze
          </h3>
          <div className="overflow-y-auto border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 flex-1">

            {experiencesLoading || showSkeleton ? (
              <div className="space-y-3">
                {Array.from(Array(3)).map((_, index) => (
                  <Skeleton key={`exp-skeleton-${index}`} className="w-full h-32" />
                ))}
              </div>
            ) : experiencesData && experiencesData.length > 0 ? (
              <div className="flex flex-col gap-3">
                {experiencesData.map((item: any, index: number) => (
                  <ExperiencesCardH
                    key={item.experience_uuid || item.uuid || `experience-${index}`}
                    data={item}
                    onDragStart={(e) => onDragStart(e)}
                    draggable={true}
                  />
                ))}
                <div className="w-full pt-4 border-t border-neutral-200 dark:border-neutral-700">
                  <ButtonPrimary onClick={handleAddNewExperience} className="w-full text-sm">
                    + Nuova esperienza
                  </ButtonPrimary>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-3">
                  Nessuna esperienza disponibile
                </p>
                <ButtonPrimary onClick={handleAddNewExperience} className="w-full text-sm">
                  + Aggiungi esperienza
                </ButtonPrimary>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <NotesModal
        isOpen={showNotesModal}
        notes={modalNotes}
        closeModal={noteModalClose}
      />
      <TimeModal
        isOpen={showTimeModal}
        time={modalTime}
        closeModal={timeModalClose}
      />
    </div>
  );
};

export default ContentTab;